import React, { useCallback, useEffect } from 'react';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CustomSelect,
  MUIDataTable,
  MUIDataTableColumn,
  Pill,
  SectionHeader,
  statusToPillVariant,
  TableActionTypes,
  TableState,
  TABLE_INITIAL_STATE,
  Typography,
  useDataTable,
} from 'web-library';
import { stringToLongDateAndShortTime } from 'web-library/utils/DateUtils';
import { useMediaQuery } from '@mui/material';
import { useHistory } from 'react-router-dom';
import RouteKey from '../../../../routes/key';
import {
  APIDropdownReturn,
  dropDownMappingPureArray,
  dropDownMappingToSelect,
  useAPIDropdown,
} from '../../../../hooks/dropdown';
import { useGetDataArchivalFilterYear } from '../../../../api-refactored/data-archival/useGetDataArchivalFilterYear';
import { useGetDataArchivalFilterArchivalLevel } from '../../../../api-refactored/data-archival/useGetDataArchivalFilterArchivalLevel';
import { customFilterChipRender } from '../../../../hooks/table';
import { usePostDataArchivalListing } from '../../../../api-refactored/data-archival/usePostDataArchivalListing';
import { SelectChangeEvent } from '@mui/material';

const { admin } = RouteKey.child;
const { dataArchival, newDataArchival, viewDataArchival } = RouteKey.admin.child;

const useStyles = makeStyles()((theme) =>
({
  container: {
    alignItems: 'center',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      marginBottom: theme.spacing(3),
    },
  },
  containerTable: {
    marginBottom: theme.spacing(7),
  },
  select: {
    marginBottom: theme.spacing(3),
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  byText: {
    color: theme.color.text.textTertiary,
  },
}));

const displayDateTimeUpdated = (
  targetDateTime: string,
  by: string,
  dataIndex: number,
  classes: Record<string,string>
) => (
  <Box key={dataIndex.toString()}>
    {stringToLongDateAndShortTime(targetDateTime)}
    <br />
    <Typography className={classes.byText} variant="subtitle2">{`by ${by}`}</Typography>
  </Box>
);

interface TableColumnProps {
  state: TableState;
  tableDispatch: React.Dispatch<TableActionTypes>;
  classes: Record<string,string>;
  t: TFunction;
  filterYear: APIDropdownReturn;
  filterArchivalLevel: APIDropdownReturn;
  navigateDetail: (uuid: string) => void;
}

const tableColumns = ({
  state,
  tableDispatch,
  classes,
  t,
  filterYear,
  filterArchivalLevel,
  navigateDetail,
}: TableColumnProps): MUIDataTableColumn[] => [
    {
      name: 'examYear',
      label: t('admin->>DataArchival.ColumnYear'),
      options: {
        sort: false,
        filter: true,
        filterType: 'custom',
        filterOptions: {
          fullWidth: true,
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <>
              <CustomSelect
                defaultValue={filterList[index][0]}
                displayEmpty
                menuItems={filterYear.functionCode}
                onChange={(event) => {
                  // eslint-disable-next-line no-param-reassign
                  filterList[index][0] = event.target.value as string;
                  onChange(filterList[index], index, column);
                }}
                label="Year"
                labelId="inputSelectLabel-request"
                id="inputSelect-request"
              />
              <Box className={classes.select} />
            </>
          ),
        },
      },
    },
    {
      name: 'examLevel',
      label: t('admin->>DataArchival.ColumnDataArchivalLevel'),
      options: {
        sort: false,
        filter: true,
        filterType: 'custom',
        customFilterListOptions: {
          render: (v: any) => [filterArchivalLevel.typeMapping[v[0]].children],
          update: (filterList: string[][],
            filterPos: number,
            index: number) => {
            // update table filter state
            tableDispatch({
              type: 'UPDATE_FILTERS',
              payload: {
                ...state.filters,
                requestType: state.filters.requestType,
              },
            });

            // update table inner filter
            return customFilterChipRender(filterList, filterPos, index);
          },
        },
        filterOptions: {
          fullWidth: true,
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <>
              <CustomSelect
                displayEmpty
                variant="filled"
                multiple
                label="Data Archival Level"
                labelId="inputSelectLabel-levelOptions"
                id="inputSelect-levelOptions"
                onChange={(e: SelectChangeEvent<unknown>) => {
                  // eslint-disable-next-line no-param-reassign
                  filterList[index] = e.target.value as string[];
                  onChange(filterList[index], index, column);
                  window.dispatchEvent(new CustomEvent('resize'));
                }}
                menuItems={filterArchivalLevel.functionCode}
                value={filterList[index]}
                renderValue={(selected: unknown | string[]) =>
                  Array.isArray(selected) && selected.length > 0
                    ? // @ts-ignore
                    filterArchivalLevel.functionCode
                      .filter((item: any) => selected.includes(item.value))
                      .map((s: any) => s.children)
                      .join(', ')
                    : 'Select Data Archival Level'
                }
              />
              <Box className={classes.select} />
            </>
          ),
        },
      },
    },
    {
      name: 'createdDateTime',
      label: t('admin->>DataArchival.ColumnCreatedDateTime'),
      options: {
        sort: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) =>
          displayDateTimeUpdated(
            state.data[dataIndex].createdDateTime,
            state.data[dataIndex].requestBy,
            dataIndex,
            classes
          ),
      },
    },
    {
      name: 'lastUpdatedDateTime',
      label: t('admin->>DataArchival.ColumnLastUpdatedDateTime'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) =>
          displayDateTimeUpdated(
            state.data[dataIndex].lastUpdatedDateTime,
            state.data[dataIndex].updatedBy,
            dataIndex,
            classes
          ),
      },
    },
    {
      name: 'status',
      label: t('admin->>DataArchival.ColumnStatus'),
      options: {
        sort: false,
        filter: false,
        setCellProps: () => ({ style: { verticalAlign: 'middle' } }),
        customBodyRenderLite: (dataIndex: number) => {
          const { status } = state.data[dataIndex];
          const variant = statusToPillVariant(status);
          if (!variant) {
            return status;
          }
          return <Pill size="M" variant={variant} label={status} />;
        },
      },
    },
    {
      name: 'actions',
      label: t('admin->>DataArchival.ColumnActions'),
      options: {
        sort: false,
        filter: false,
        setCellProps: () => ({ style: { verticalAlign: 'middle' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Button
            id="button-manage-notifications-view"
            onClick={() => {
              navigateDetail(state.data[dataIndex].uuid);
            }}
            variant="text"
            color="primary"
            size="small"
          >
            {t('admin->>DataArchival.ViewDetails')}
          </Button>
        ),
      },
    },
  ];

const useRawFilters = () => {
  const filterYearResponse = useGetDataArchivalFilterYear({});
  const filterYear = useAPIDropdown(
    () => ({ data: filterYearResponse.data }),
    true,
    dropDownMappingPureArray
  );

  const filterArchivalLevelResponse = useGetDataArchivalFilterArchivalLevel({});

  const filterArchivalLevel = useAPIDropdown(
    () => ({ data: filterArchivalLevelResponse.data }),
    true,
    dropDownMappingToSelect
  );

  return {
    filterYear,
    filterArchivalLevel,
  };
};

const ListDataArchival = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // state
  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'createdDateTime',
    order: 'desc',
  });

  // api
  const { filterYear, filterArchivalLevel } = useRawFilters();
  const { mutate: listMutate, isLoading: listLoading } = usePostDataArchivalListing({
    onSuccess: (data) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });

  // effect
  useEffect(() => {
    listMutate({
      apiPathParam: {
        page: state.page,
        pageSize: state.size,
        sort: state.sortColumn,
        order: state.order,
      },
      examYear:
        state.filters.examYear && state.filters.examYear.length > 0
          ? state.filters.examYear[0]
          : null,
      examLevels: state.filters.examLevel || [],
    });
  }, [state.order, state.page, state.size, state.sortColumn, state.filters, listMutate]);

  // callback
  const navigateNewDataArchival = useCallback(
    () => history.push(`/${admin}/${dataArchival.route}/${newDataArchival.route}`),
    [history]
  );
  const navigateDataArchivalDetail = useCallback(
    (uuid: string) =>
      history.push(`/${admin}/${dataArchival.route}/${uuid}/${viewDataArchival.route}`),
    [history]
  );

  return (
    <Box className={classes.container}>
      <Box className={classes.headerContainer}>
        <SectionHeader title={t('admin->>DataArchival.DataArchivalLog')} />
        <Button
          onClick={navigateNewDataArchival}
          id="button-new-data-archival"
          size="medium"
          fullWidth={isMobile}
        >
          {t('admin->>DataArchival.NewDataArchival')}
        </Button>
      </Box>

      <Box className={classes.containerTable}>
        <MUIDataTable
          state={state}
          isLoading={listLoading}
          dispatch={tableDispatch}
          columns={tableColumns({
            state,
            tableDispatch,
            classes,
            t,
            filterYear,
            filterArchivalLevel,
            navigateDetail: navigateDataArchivalDetail,
          })}
          data={state.data}
          options={{
            ...tOptions,
            filter: true,
            sort: true,
            pagination: true,
            sortOrder: {
              name: state.sortColumn,
              direction: state.order,
            },
          }}
          emptyDataLabel={t('admin->>DataArchival.EmptyDataArchival')}
          rowsPerPageOptions={[5, 10, 20, 40, 50]}
        />
      </Box>
    </Box>
  );
};

export default ListDataArchival;
